import { BuildHomeHeader, BuildNav, DormantRoles, PhaseIndex, PublishedResources, StageOf, type IOEPResource, type IOEPRoleResources, type IOEPTimelineMilestone, type OEPPhase, type OEPRoleKey, type OEPTask, type OEPUserSnapshot } from 'agu_oep_core';
import { DayMonth } from '../shared/format';

/** A line in "Already done": Title is the fact, When its date (US Eastern, "28 October") or '' when it has none. */
export type OEPDoneItem = { Title: string; When: string };

export interface OEPHomeVM {
    Greeting: string;
    Deadline: { Label: string; Value: string };
    Season: { Stage: string; Months: string; Items: { Label: string; Note: string; On: boolean; Next: boolean }[] };
    Tasks: OEPTask[];
    NoTasks: boolean;
    NextUpNote: string;
    Guidance: IOEPResource[];
    /** Whether the intro may point at the Resources tab: true exactly when the nav shows it. */
    ResourcesTabNote: boolean;
    Done: OEPDoneItem[];
    Dormant: { Role: OEPRoleKey; Label: string; Opens: string }[];
}

const EASTERN = 'America/New_York';

/** logic.js:521 is a static "Good morning"; choosing by the US Eastern hour of Now is the one non-copy change. */
export function OEPGreeting(first: string, now: Date): string {
    const hour = Number(new Intl.DateTimeFormat('en-US', { timeZone: EASTERN, hour: 'numeric', hourCycle: 'h23' }).format(now));
    const part = hour < 12 ? 'morning' : hour < 17 ? 'afternoon' : 'evening';
    return `Good ${part}, ${first}`;
}

/** Milestones a presenter who is not also a reviewer does not see (client edit 2026-10-27): matching and winner selection. */
const PRESENTER_HIDDEN_PHASES: ReadonlySet<OEPPhase> = new Set<OEPPhase>(['Matched', 'Selecting']);

function visibleMilestones(s: OEPUserSnapshot): IOEPTimelineMilestone[] {
    const presenterOnly = s.Roles.has('presenter') && !s.Roles.has('reviewer');
    return presenterOnly ? s.Timeline.Milestones.filter((m) => !PRESENTER_HIDDEN_PHASES.has(m.Phase)) : s.Timeline.Milestones;
}

const plural = (n: number, one: string, many: string): string => `${n} ${n === 1 ? one : many}`;

/**
 * Only events the platform records. The prototype's DONE list (logic.js:437-460) also has
 * "application submitted" and "training completed", which nothing in the data model records, so they are omitted.
 */
export function BuildDoneItems(i: { Snapshot: OEPUserSnapshot; DraftAt: Date | null; FinalAt: Date | null }): OEPDoneItem[] {
    const s = i.Snapshot;
    const out: OEPDoneItem[] = [];
    if (s.Roles.has('presenter') && s.Presenter) {
        if (s.Presenter.HasDraft && i.DraftAt) out.push({ Title: 'Draft uploaded', When: DayMonth(i.DraftAt) });
        if (s.Presenter.HasFinal && i.FinalAt) out.push({ Title: 'Final uploaded', When: DayMonth(i.FinalAt) });
    }
    if (s.Roles.has('reviewer') && s.Reviewer) {
        const n = s.Reviewer.LiveAssignments - s.Reviewer.UnsubmittedReviews;
        if (n > 0) out.push({ Title: `${plural(n, 'review', 'reviews')} submitted`, When: '' });
    }
    if (s.Roles.has('nominator') && s.Nominator && s.Nominator.SubmittedNominations > 0) {
        out.push({ Title: `${plural(s.Nominator.SubmittedNominations, 'nomination', 'nominations')} submitted`, When: '' });
    }
    if (s.Roles.has('committee') && s.Committee?.SlateSubmitted) out.push({ Title: 'Winner slate submitted', When: '' });
    return out;
}

/** markup.html:202-299 / logic.js:268-273, 521-538, in one pure function. */
export function BuildHomeVM(i: { FirstName: string; Now: Date; Snapshot: OEPUserSnapshot; Tasks: OEPTask[]; Resources: IOEPRoleResources; Done: OEPDoneItem[] }): OEPHomeVM {
    const s = i.Snapshot;
    const stage = StageOf(s.Phase);
    const idx = PhaseIndex(s.Phase);
    const milestones = visibleMilestones(s);
    const current = milestones.reduce<number>((best, m, i) => (PhaseIndex(m.Phase) <= idx && (best < 0 || PhaseIndex(m.Phase) > PhaseIndex(milestones[best]!.Phase)) ? i : best), -1);
    const header = BuildHomeHeader(s, i.Tasks);
    const dormant = DormantRoles(s, i.Tasks);
    const active = [...new Set(i.Tasks.map((t) => t.Role))]; // logic.js:533 activeResources: first two per active role, max three
    return {
        Greeting: OEPGreeting(i.FirstName, i.Now),
        Deadline: { Label: header.DeadlineLabel, Value: header.NextDeadline },
        Season: {
            Stage: s.Timeline.Stages[stage].Label,
            Months: s.Timeline.Stages[stage].Months,
            Items: milestones.map((m, i) => ({ Label: m.Label, Note: m.Note, On: i === current, Next: current < 0 && i === 0 })),
        },
        Tasks: i.Tasks,
        NoTasks: i.Tasks.length === 0,
        NextUpNote: dormant.length ? `${dormant[0].Label} ${dormant[0].Opens}.` : '',
        // Presenter links still on the placeholder Url are hidden (client edit 2026-10-27); other roles unchanged.
        Guidance: active.flatMap((r) => (r === 'presenter' ? PublishedResources(i.Resources[r] ?? []) : (i.Resources[r] ?? [])).slice(0, 2)).slice(0, 3),
        ResourcesTabNote: BuildNav(s).some((n) => n.View === 'resources'),
        Done: i.Done,
        Dormant: dormant,
    };
}
